import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { siteInfo } from '@/mocks/articles';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt')?.toString()?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9sqtvl8o1vn02ekukmg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(String(msg));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://powen-tuijian.com',
    description: siteInfo.description,
    foundingDate: siteInfo.foundedYear,
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - po文推荐"
        description={`${siteInfo.name}于${siteInfo.foundedYear}年创立，致力于为用户提供精选高质量po文推荐。涵盖内容创作、写作技巧、阅读方法等多个领域，帮您告别信息过载，发现真正值得阅读的好内容。`}
        keywords="关于po文推荐,po文推荐团队,内容推荐平台,品牌介绍"
        canonicalUrl="https://powen-tuijian.com/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />

            <header className="mb-12">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-3xl leading-relaxed">
                了解{siteInfo.name}的品牌故事、核心团队以及我们的使命——让每一位读者都能高效发现优质内容。
              </p>
            </header>

            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                品牌介绍
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    <strong className="text-foreground-900">{siteInfo.name}</strong>创立于{siteInfo.foundedYear}年，是一个专注于优质内容推荐与聚合的平台。在信息过载的时代，我们深知"选择困难症"是每个内容消费者的痛点——每天面对海量信息，却很难找到真正值得阅读的高质量内容。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    我们的<strong className="text-foreground-900">po文推荐清单</strong>由专业编辑团队精心筛选，结合大数据分析，确保每一篇推荐都经过严格的质量把关。从内容创作、写作技巧到阅读方法、创作者生活，我们覆盖多个热门领域，致力于成为您最信赖的内容发现伙伴。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    无论是寻找灵感、拓展视野，还是纯粹的阅读享受，{siteInfo.name}都希望成为您日常内容消费的起点——告别信息过载，每次刷新都有新惊喜。
                  </p>
                </div>
                <div className="rounded-xl overflow-hidden h-64 lg:h-full">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20modern%20office%20space%20with%20team%20collaboration%2C%20creative%20workspace%2C%20soft%20natural%20lighting%2C%20indoor%20plants%2C%20wooden%20furniture%2C%20cozy%20atmosphere%2C%20editorial%20photography%2C%20warm%20terracotta%20and%20cream%20tones%2C%20no%20visible%20faces&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt={`${siteInfo.name} 办公环境 - 精选高质量内容推荐`}
                    title={`${siteInfo.name} - 精选高质量内容推荐`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </section>

            <section className="mb-16 py-12 px-6 md:px-10 bg-background-100/70 rounded-2xl">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "每天都会打开po文推荐看看今天的推荐清单，已经成了我的阅读习惯。内容质量很高，省去了自己筛选的时间，真心推荐给所有内容爱好者。"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-accent-600">李</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-800">李先生</p>
                      <p className="text-xs text-foreground-400">内容创作者</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "作为自媒体运营者，po文推荐是我获取灵感和行业动态的重要渠道。分类清晰，更新及时，特别是精选合集板块，经常能发现一些平时刷不到的优质内容。"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">王</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-800">王女士</p>
                      <p className="text-xs text-foreground-400">自媒体运营</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "界面简洁，没有广告干扰，阅读体验非常好。推荐的每一篇文章都值得仔细阅读，不像其他平台那样充斥着标题党和低质内容。"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-secondary-600">张</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-800">张先生</p>
                      <p className="text-xs text-foreground-400">产品经理</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="space-y-4 mb-6">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">邮箱</p>
                        <p className="text-sm text-foreground-500">{siteInfo.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">地址</p>
                        <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-4"
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>

                    <div className="honeypot-wrapper">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'loading' ? '提交中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center">感谢您的留言，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError || '提交失败，请稍后重试'}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}